import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft, ArrowRight, Laptop, Lock, KeyRound, Shield,
  Users, CheckCircle2, AlertTriangle, XCircle, Settings, Rocket, RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import macosHeroBg from "@/assets/macos-hero-bg-new.jpg";
import appleLogo from "@/assets/ms-icons/apple.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";
import abmIcon from "@/assets/ms-icons/apple-business-manager.png";
import filevaultIcon from "@/assets/ms-icons/filevault.png";

const voordelen = [
  {
    icon: abmIcon,
    title: "Automatische inrichting via Apple Business Manager",
    text: "Macs worden automatisch gekoppeld aan Apple Business Manager. Het device schrijft zich bij opstart automatisch in bij Intune. De medewerker kan het apparaat nooit claimen met een persoonlijk Apple ID. Activation Lock staat altijd op naam van de organisatie.",
  },
  {
    icon: KeyRound,
    title: "Single Sign-On voor Microsoft 365",
    text: "Medewerkers loggen één keer in met hun Microsoft 365-account en zijn direct aangemeld in Outlook, Teams, OneDrive, SharePoint en andere Microsoft-apps. Geen gedoe met lokale accounts of losse wachtwoorden.",
  },
  {
    icon: Settings,
    title: "Automatische installatie van Office-apps",
    text: "Word, Excel, Outlook en Teams worden automatisch geïnstalleerd via Intune. Geen installatietijd of handmatige downloads. Altijd de juiste versie en instellingen.",
  },
  {
    icon: defenderIcon,
    title: "Microsoft Defender for Business automatisch actief",
    text: "Defender wordt automatisch geïnstalleerd en geconfigureerd. Bedreigingen worden real-time gedetecteerd en geblokkeerd. Medewerkers kunnen beveiligingsinstellingen niet per ongeluk uitschakelen.",
  },
  {
    icon: filevaultIcon,
    title: "Volledige beveiliging met FileVault",
    text: "De schijf wordt automatisch versleuteld zodra het device is ingeschreven. De organisatie ontvangt automatisch de FileVault-herstelcode via Intune. Data blijft veilig bij verlies of diefstal.",
  },
  {
    icon: onedriveIcon,
    title: "OneDrive volledig automatisch ingesteld",
    text: "OneDrive wordt automatisch ingelogd met het Microsoft-account van de medewerker. Bureaublad, Documenten en Afbeeldingen worden automatisch gesynchroniseerd. Bij een device-wisseling zie je direct al je bestanden op dezelfde plek.",
  },
  {
    icon: sharepointIcon,
    title: "SharePoint direct in Finder",
    text: "Net als bij Windows wordt SharePoint automatisch gekoppeld. Gedeelde mappen verschijnen als locaties in de Finder. Geen handmatig sync-werk of gebruikersfouten.",
  },
  {
    icon: Rocket,
    title: "Nieuwe devices binnen minuten inzetbaar",
    text: "Een medewerker hoeft alleen in te loggen met zijn bedrijfsaccount. Alle apps, instellingen en beveiliging worden automatisch toegepast. Ideaal voor thuiswerken en hybride teams.",
  },
  {
    icon: RefreshCw,
    title: "Consistente en foutloze inrichting",
    text: "Geen fouten door handmatige installatie. Iedere Mac wordt identiek en volgens beleid ingericht. Veel minder beheerwerk voor IT.",
  },
  {
    icon: Users,
    title: "Eén Mac voor meerdere medewerkers",
    text: "Macs die via Intune en Apple Business Manager zijn ingericht, kunnen door meerdere gebruikers worden gebruikt. Een nieuwe medewerker logt in en zijn instellingen worden automatisch geladen. Bij vertrek kan de Mac veilig worden gewist en opnieuw worden uitgedeeld.",
  },
];

const metVoordelen = [
  "Bedrijf behoudt volledige eigendomscontrole",
  "Activation Lock altijd in handen van de organisatie",
  "Automatische Intune-inschrijving (zero-touch)",
  "Veilig gebruik van persoonlijk Apple ID",
  "Macs direct inzetbaar voor nieuwe medewerkers",
  "Veilig, automatisch en centraal beheerd",
  "FileVault automatisch geconfigureerd",
  "OneDrive + SharePoint automatisch geïntegreerd",
  "Multi-user gebruik mogelijk",
];

const zonderNadelen = [
  "Mac kan permanent worden gelocked door medewerker",
  "Activation Lock niet te verwijderen",
  "Apparaten worden onbruikbaar bij uitdiensttreding",
  "Niet overdraagbaar, niet opnieuw in te richten",
  "Grote financiële schade en datarisico",
];

const WerkplekMacOS = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <div className="absolute inset-0">
          <img loading="eager" decoding="async" fetchPriority="high" src={macosHeroBg} alt="IT-beheerder beheert Apple Mac-apparaten" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(210,25%,12%)]/90 via-[hsl(210,30%,18%)]/80 to-[hsl(204,60%,30%)]/70" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Persoonlijk Apple ID? Veilig mogelijk
              </span>
            </motion.div>

            <motion.div
              className="mt-4 mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 p-2.5"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <img loading="lazy" decoding="async" src={appleLogo} alt="Apple" className="h-8 w-auto invert" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Apple macOS{" "}
              <span className="text-white/90">beheer</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Macs worden via Apple Business Manager en Microsoft Intune volledig automatisch ingericht, beveiligd en beheerd. Het bedrijf behoudt 100% controle — altijd.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Apple Business Manager zero-touch
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                FileVault-versleuteling standaard
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Single Sign-On voor Microsoft 365
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Voordelen van <span className="text-gradient">Intune + Apple Business Manager</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              De Mac werkt net als een moderne Windows-werkplek. Automatisch ingericht, beveiligd en altijd onder controle van de organisatie.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2">
            {voordelen.map((item, i) => {
              const isImageIcon = typeof item.icon === "string";
              const colors = ["bg-hero-gradient", "bg-rinqel-blue-gradient", "bg-rinqel-green-gradient"];
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  {isImageIcon ? (
                    <img loading="lazy" decoding="async" src={item.icon as string} alt="" className="h-11 w-11 shrink-0" />
                  ) : (
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${colors[i % 3]} text-primary-foreground`}>
                      {(() => { const Icon = item.icon as React.ComponentType<{ className?: string }>; return <Icon className="h-5 w-5" />; })()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Persoonlijk Apple ID */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Persoonlijk Apple ID?{" "}
              <span className="text-gradient">Veilig mogelijk</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Veel bedrijven willen dat medewerkers hun persoonlijke Apple ID kunnen gebruiken voor Favorieten, iCloud-voorkeuren, Safari-instellingen en Handoff-functies. Met Apple Business Manager kan dit veilig, omdat het bedrijf eigenaar blijft en Activation Lock altijd door de organisatie wordt beheerd. Een medewerker kan het apparaat nooit "vergrendelen".
            </p>
          </motion.div>
        </div>
      </section>

      {/* Wissen op afstand */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-blue-gradient text-primary-foreground">
              <Shield className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Op afstand <span className="text-gradient">wissen</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Mac kwijt, gestolen of een medewerker gaat uit dienst? Via Intune kunnen we elk apparaat op afstand volledig wissen, zodat alle bedrijfsdata veilig wordt verwijderd. Het apparaat kan daarna direct opnieuw worden ingericht voor een nieuwe medewerker.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vergelijking: Met vs Zonder ABM */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Waarom Apple Business Manager{" "}
            <span className="text-gradient">essentieel</span> is
          </motion.h2>

          <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
            {/* Met ABM */}
            <motion.div
              className="rounded-xl border border-rinqel-green/30 bg-card p-6 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rinqel-green/10">
                  <CheckCircle2 className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="font-bold text-rinqel-green">Met Apple Business Manager + Intune</h3>
              </div>
              <ul className="space-y-2">
                {metVoordelen.map((v) => (
                  <li key={v} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Zonder ABM */}
            <motion.div
              className="rounded-xl border border-destructive/30 bg-card p-6 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="font-bold text-destructive">Zonder Apple Business Manager</h3>
              </div>
              <ul className="space-y-2">
                {zonderNadelen.map((n) => (
                  <li key={n} className="flex items-start gap-2 text-sm">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 rounded-lg bg-destructive/5 p-3">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-destructive">Let op:</span> Zonder Apple Business Manager kan een medewerker de Mac permanent claimen met een persoonlijk Apple ID. Bij vertrek, conflict of overlijden kan het apparaat onbruikbaar worden. Apple kan de Mac niet ontgrendelen, zelfs niet met aankoopfactuur. Dit kan duizenden euro's schade betekenen.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-macos-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekMacOS;
